<template>
	<div class="user-view-control d-flex align-center ga-2 ml-4">
		<SelectControl :model-value="selectedViewId" :label="t('UserViews')" :items="viewSelectItems" title-key="name" density="comfortable" :sort-items="false" :clearable="false" :readonly="isTablet" class="user-view-control__select" :class="{ 'user-view-control__select--sheet': isTablet }" :disabled="!props.isReady || isApplying" @update:model-value="onSelectedViewChanged" @click="onSelectClick" />

		<v-menu :attach="overlayTarget || undefined" location="bottom end">
			<template v-slot:activator="{ props: menuProps }">
				<v-btn v-bind="menuProps" density="comfortable" icon="more_vert" variant="tonal" color="primary" :disabled="!props.isReady" />
			</template>
			<v-list density="compact" min-width="220">
				<v-list-item :title="t('AddUserView')" :disabled="!props.isReady || isSaving" @click="onAddClick">
					<template v-slot:prepend>
						<v-icon icon="add" color="success" />
					</template>
				</v-list-item>
				<v-list-item :title="t('Save')" :disabled="!props.isReady || !selectedPersistedView || !isSelectedViewDirty || isSaving" @click="onSaveClick">
					<template v-slot:prepend>
						<v-icon icon="save" color="primary" />
					</template>
				</v-list-item>
				<v-list-item :title="t('Edit')" :disabled="!props.isReady || !selectedPersistedView || isSaving" @click="onEditClick">
					<template v-slot:prepend>
						<v-icon icon="edit" color="info" />
					</template>
				</v-list-item>
				<v-list-item :title="t('Delete')" :disabled="!props.isReady || !selectedPersistedView || isSaving" @click="onDeleteClick">
					<template v-slot:prepend>
						<v-icon icon="delete" color="error" />
					</template>
				</v-list-item>
			</v-list>
		</v-menu>

		<slot name="actions" />
	</div>

	<v-bottom-sheet v-model="viewSheetOpen" :attach="overlayTarget || undefined" class="user-view-control__sheet-overlay">
		<v-card class="user-view-control__sheet">
			<v-card-title class="user-view-control__sheet-title">
				<v-icon>bookmark</v-icon>
				{{ t('UserViews') }}
				<v-chip size="small" color="primary" variant="tonal">{{ viewSelectItems.length }}</v-chip>
				<v-spacer></v-spacer>
				<v-btn icon="close" variant="tonal" color="primary" density="comfortable" @click="viewSheetOpen = false" />
			</v-card-title>

			<v-list class="user-view-control__sheet-list" density="comfortable">
				<v-list-item v-for="view in viewSelectItems" :key="view.id" :active="view.id === selectedViewId" color="primary" @click="onViewSheetSelect(view.id)">
					<v-list-item-title>{{ view.name }}</v-list-item-title>
				</v-list-item>
			</v-list>

			<v-card-actions class="user-view-control__sheet-actions">
				<v-spacer></v-spacer>
				<v-btn variant="tonal" color="primary" :disabled="!props.isReady || isSaving" @click="onSheetAddClick">{{ t('AddUserView') }}</v-btn>
			</v-card-actions>
		</v-card>
	</v-bottom-sheet>

	<DialogControl v-model="dialogOpen" :max-width="'400px'" :attach="overlayTarget || undefined">
		<v-card>
			<v-card-title>
				<v-icon>table_eye</v-icon>
				{{ mode === 'add' ? t('AddUserView') : t('EditUserView') }}
			</v-card-title>
			<v-card-text>
				<v-text-field v-model="name" minlength="3" maxlength="50" counter :label="t('Name')" required variant="outlined" density="comfortable" />
			</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="dialogOpen = false" data-close>{{ t('Close') }}</v-btn>
				<v-btn variant="tonal" @click="saveView" :disabled="name.trim().length < 3 || isSaving">{{ t('Save') }}</v-btn>
			</v-card-actions>
		</v-card>
	</DialogControl>

	<DialogControl v-model="deleteDialogOpen" :max-width="'400px'" :attach="overlayTarget || undefined">
		<v-card>
			<v-card-title>
				<v-icon>warning</v-icon>
				{{ t('Delete') }}
			</v-card-title>
			<v-card-text>{{ t('AreYouSureYouWantToDelete') }} {{ t('TheUserView') }} {{ selectedPersistedView?.name || '' }} ?</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="deleteDialogOpen = false">{{ t('Close') }}</v-btn>
				<v-btn color="error" variant="tonal" :disabled="isSaving" @click="deleteSelectedView">{{ t('Delete') }}</v-btn>
			</v-card-actions>
		</v-card>
	</DialogControl>
</template>

<script setup>
import { inject, computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useTranslator } from '@/composables/useTranslator';
import { getUserSettings, postUserSettings } from '@/api/core/userSetting';
import { log } from '@/utils/core/vueLogger.js';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { useOverlayScrimClose } from '@/composables/useOverlayScrimClose';
import SelectControl from '@/components/controls/SelectControl.vue';
import DialogControl from '@/components/controls/DialogControl.vue';

const DEFAULT_VIEW_ID = '__default__';

const props = defineProps({
	viewKey: { type: String, required: true },
	groupName: { type: String, default: 'WEBAPP-UserViews' },
	getViewState: { type: Function, default: null },
	applyViewState: { type: Function, default: null },
	defaultViewState: { type: [Object, Function], default: null },
	isReady: { type: Boolean, default: true },
	stateVersion: { type: Number, default: 0 },
});

const emit = defineEmits(['view-applied', 'view-saved', 'view-deleted', 'view-error']);

const t = useTranslator();
const user = iframeDataBridge.getCurrentUser();
const overlayTarget = inject('overlayTarget', null);
const isTablet = ref(deviceUtils.isTablet());
const onResize = () => {
	isTablet.value = deviceUtils.isTablet();
};

const dialogOpen = ref(false);
const deleteDialogOpen = ref(false);
const viewSheetOpen = ref(false);
const settings = ref([]);
const views = ref([]);
const selectedViewId = ref(null);
const mode = ref('add');
const name = ref('');
const editingViewId = ref(null);
const isSaving = ref(false);
const isApplying = ref(false);
const pendingViewIdToApply = ref(null);
const defaultViewState = ref(null);
const isSelectedViewDirty = ref(false);

const defaultViewLabel = computed(() => {
	return t('DefaultView');
});
const viewSelectItems = computed(() => [{ id: DEFAULT_VIEW_ID, name: defaultViewLabel.value }, ...views.value.map((view) => ({ id: view.id, name: view.name }))]);
const selectedPersistedView = computed(() => views.value.find((view) => view.id === selectedViewId.value) || null);
useOverlayScrimClose(
	viewSheetOpen,
	() => {
		viewSheetOpen.value = false;
	},
	overlayTarget,
	'.user-view-control__sheet-overlay'
);

onMounted(async () => {
	await loadViews();
	window.addEventListener('resize', onResize);
});

onUnmounted(() => {
	window.removeEventListener('resize', onResize);
});

watch(
	() => props.isReady,
	async (isReady) => {
		if (isReady && !defaultViewState.value) {
			const initialState = await captureCurrentViewState();
			if (initialState) {
				defaultViewState.value = initialState;
			}
		}

		if (!isReady || !pendingViewIdToApply.value) {
			return;
		}

		const viewId = pendingViewIdToApply.value;
		pendingViewIdToApply.value = null;
		await applySelectedView(viewId, { persistSelection: true });
		await refreshDirtyState();
	}
);

watch(
	() => props.stateVersion,
	async () => {
		await refreshDirtyState();
	}
);

watch(
	() => selectedViewId.value,
	async () => {
		await refreshDirtyState();
	}
);

function openAddViewDialog() {
	mode.value = 'add';
	editingViewId.value = null;
	name.value = '';
	dialogOpen.value = true;
}

function onAddClick() {
	openAddViewDialog();
}

function onSaveClick() {
	updateSelectedView();
}

function onEditClick() {
	openEditViewDialog();
}

function onDeleteClick() {
	deleteDialogOpen.value = true;
}

// The select keeps its desktop look on mobile but its own menu is off (readonly): it opens the sheet.
function onSelectClick() {
	if (!isTablet.value || !props.isReady || isApplying.value) {
		return;
	}

	viewSheetOpen.value = true;
}

function onSheetAddClick() {
	viewSheetOpen.value = false;
	openAddViewDialog();
}

async function onViewSheetSelect(viewId) {
	viewSheetOpen.value = false;
	await onSelectedViewChanged(viewId);
}

function openEditViewDialog() {
	if (!selectedPersistedView.value) {
		return;
	}

	mode.value = 'edit';
	editingViewId.value = selectedPersistedView.value.id;
	name.value = selectedPersistedView.value.name;
	dialogOpen.value = true;
}

function normalizeViews(rawValue) {
	if (!rawValue) {
		return [];
	}

	try {
		const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
		if (!Array.isArray(parsed)) {
			return [];
		}

		return parsed
			.map((view) => {
				if (!view || typeof view !== 'object') {
					return null;
				}

				const normalizedName = String(view.name || '').trim();
				if (!normalizedName) {
					return null;
				}

				return {
					id: view.id || crypto.randomUUID(),
					name: normalizedName,
					state: view.state ?? null,
					createdAt: view.createdAt || new Date().toISOString(),
					updatedAt: view.updatedAt || new Date().toISOString(),
				};
			})
			.filter(Boolean);
	} catch {
		return [];
	}
}

async function loadViews() {
	try {
		settings.value = await getUserSettings({ groupName: props.groupName, userId: user.id });
		const viewSetting = (Array.isArray(settings.value) ? settings.value : []).find((setting) => setting.name === props.viewKey);
		views.value = normalizeViews(viewSetting?.value);

		if (props.isReady && !defaultViewState.value) {
			const initialState = await captureCurrentViewState();
			if (initialState) {
				defaultViewState.value = initialState;
			}
		}

		const lastSelectedViewId = getLastSelectedViewId();
		if (!lastSelectedViewId) {
			selectedViewId.value = DEFAULT_VIEW_ID;
			return;
		}

		const isDefaultSelection = lastSelectedViewId === DEFAULT_VIEW_ID;
		const hasPersistedSelection = views.value.some((view) => view.id === lastSelectedViewId);
		if (!isDefaultSelection && !hasPersistedSelection) {
			clearLastSelectedViewId();
			selectedViewId.value = null;
			return;
		}

		selectedViewId.value = lastSelectedViewId;
		if (isDefaultSelection) {
			await refreshDirtyState();
			return;
		}

		if (props.isReady) {
			await applySelectedView(lastSelectedViewId, { persistSelection: true });
		} else {
			pendingViewIdToApply.value = lastSelectedViewId;
		}

		await refreshDirtyState();
	} catch (error) {
		log('error', 'UserViewControl', 'Failed to load user views');
	}
}

function getStorageKey() {
	return `WEBAPP-UserViews:lastSelected:${user?.id || 'anonymous'}:${props.viewKey}`;
}

function getLastSelectedViewId() {
	try {
		return localStorage.getItem(getStorageKey());
	} catch {
		return null;
	}
}

function setLastSelectedViewId(viewId) {
	try {
		if (!viewId) {
			localStorage.removeItem(getStorageKey());
			return;
		}

		localStorage.setItem(getStorageKey(), String(viewId));
	} catch {
		// localStorage may be unavailable depending on browser context.
	}
}

function clearLastSelectedViewId() {
	setLastSelectedViewId(null);
}

function safeClone(value) {
	if (value === null || value === undefined) {
		return value;
	}

	try {
		return JSON.parse(JSON.stringify(value));
	} catch {
		return value;
	}
}

async function persistViews() {
	const nextSettings = (Array.isArray(settings.value) ? settings.value : []).filter((setting) => setting.name !== props.viewKey);
	nextSettings.push({
		userId: user.id,
		groupName: props.groupName,
		name: props.viewKey,
		value: JSON.stringify(views.value),
	});

	const isSaved = await postUserSettings(nextSettings, { groupName: props.groupName, userId: user.id });
	if (!isSaved) {
		log('error', 'UserViewControl', 'Failed to save user views');
		return false;
	}

	settings.value = nextSettings;
	return true;
}

async function captureCurrentViewState() {
	if (typeof props.getViewState !== 'function') {
		log('error', 'UserViewControl', 'Failed to save view: capture unavailable');
		return null;
	}

	try {
		return await props.getViewState();
	} catch (error) {
		log('error', 'UserViewControl', 'Failed to save current view');
		return null;
	}
}

function getNormalizedStateFingerprint(state) {
	if (state === null || state === undefined) {
		return '';
	}

	const normalizeObject = (value) => {
		if (Array.isArray(value)) {
			return value.map((entry) => normalizeObject(entry));
		}

		if (!value || typeof value !== 'object') {
			return value;
		}

		const normalized = {};
		Object.keys(value)
			.sort((left, right) => left.localeCompare(right))
			.forEach((key) => {
				normalized[key] = normalizeObject(value[key]);
			});

		return normalized;
	};

	return JSON.stringify(normalizeObject(state));
}

async function refreshDirtyState() {
	if (!props.isReady || isApplying.value || !selectedPersistedView.value) {
		isSelectedViewDirty.value = false;
		return;
	}

	const currentState = await captureCurrentViewState();
	if (!currentState) {
		isSelectedViewDirty.value = false;
		return;
	}

	const persistedFingerprint = getNormalizedStateFingerprint(selectedPersistedView.value.state);
	const currentFingerprint = getNormalizedStateFingerprint(currentState);
	isSelectedViewDirty.value = persistedFingerprint !== currentFingerprint;
}

async function saveView() {
	const normalizedName = name.value.trim();
	if (normalizedName.length < 3) {
		return;
	}

	isSaving.value = true;
	try {
		const state = await captureCurrentViewState();
		if (!state) {
			return;
		}

		const existingViewWithSameName = views.value.find((view) => view.name.toLowerCase() === normalizedName.toLowerCase() && view.id !== editingViewId.value);
		if (existingViewWithSameName) {
			log('error', 'UserViewControl', 'A view with this name already exists');
			return;
		}

		if (mode.value === 'edit' && editingViewId.value) {
			views.value = views.value.map((view) => {
				if (view.id !== editingViewId.value) {
					return view;
				}

				return {
					...view,
					name: normalizedName,
					state: state,
					updatedAt: new Date().toISOString(),
				};
			});
			selectedViewId.value = editingViewId.value;
			setLastSelectedViewId(editingViewId.value);
		} else {
			const createdView = {
				id: crypto.randomUUID(),
				name: normalizedName,
				state: state,
				createdAt: new Date().toISOString(),
				updatedAt: new Date().toISOString(),
			};
			views.value = [...views.value, createdView];
			selectedViewId.value = createdView.id;
			setLastSelectedViewId(createdView.id);
		}

		const saved = await persistViews();
		if (!saved) {
			return;
		}

		emit('view-saved', selectedPersistedView.value);
		dialogOpen.value = false;
		isSelectedViewDirty.value = false;
	} finally {
		isSaving.value = false;
	}
}

async function onSelectedViewChanged(viewId) {
	selectedViewId.value = viewId || null;

	if (!viewId) {
		clearLastSelectedViewId();
		isSelectedViewDirty.value = false;
		return;
	}

	if (!props.isReady) {
		setLastSelectedViewId(viewId);
		pendingViewIdToApply.value = viewId;
		isSelectedViewDirty.value = false;
		return;
	}

	await applySelectedView(viewId, { persistSelection: true });
	await refreshDirtyState();
}

async function applySelectedView(viewId, options = {}) {
	const persistSelection = options?.persistSelection !== false;
	const isDefaultView = viewId === DEFAULT_VIEW_ID;
	const targetView = views.value.find((view) => view.id === viewId);
	if ((!isDefaultView && !targetView) || typeof props.applyViewState !== 'function') {
		return;
	}

	isApplying.value = true;
	try {
		if (isDefaultView) {
			const defaultStateToApply = await resolveDefaultViewState();
			if (!defaultStateToApply) {
				return;
			}
			await props.applyViewState(safeClone(defaultStateToApply));
		} else {
			await props.applyViewState(safeClone(targetView.state));
		}

		if (persistSelection) {
			setLastSelectedViewId(viewId);
		}

		emit('view-applied', isDefaultView ? { id: DEFAULT_VIEW_ID, name: defaultViewLabel.value } : targetView);
		isSelectedViewDirty.value = false;
	} catch (error) {
		log('error', 'UserViewControl', 'Failed to apply user view');
	} finally {
		isApplying.value = false;
	}
}

async function resolveDefaultViewState() {
	if (typeof props.defaultViewState === 'function') {
		try {
			const resolvedState = await props.defaultViewState();
			if (resolvedState) {
				return resolvedState;
			}
		} catch (error) {
			log('error', 'UserViewControl', 'Failed to resolve default user view');
		}
	}

	if (props.defaultViewState && typeof props.defaultViewState === 'object') {
		return props.defaultViewState;
	}

	return defaultViewState.value || (await captureCurrentViewState());
}

async function updateSelectedView() {
	if (!selectedPersistedView.value) {
		return;
	}

	isSaving.value = true;
	try {
		const currentState = await captureCurrentViewState();
		if (!currentState) {
			return;
		}

		const selectedId = selectedPersistedView.value.id;
		views.value = views.value.map((view) => {
			if (view.id !== selectedId) {
				return view;
			}

			return {
				...view,
				state: currentState,
				updatedAt: new Date().toISOString(),
			};
		});

		const saved = await persistViews();
		if (!saved) {
			return;
		}

		emit('view-saved', selectedPersistedView.value);
		isSelectedViewDirty.value = false;
	} finally {
		isSaving.value = false;
	}
}

async function deleteSelectedView() {
	if (!selectedPersistedView.value) {
		deleteDialogOpen.value = false;
		return;
	}

	isSaving.value = true;
	try {
		const removedViewId = selectedPersistedView.value.id;
		views.value = views.value.filter((view) => view.id !== removedViewId);
		selectedViewId.value = DEFAULT_VIEW_ID;
		setLastSelectedViewId(DEFAULT_VIEW_ID);

		const saved = await persistViews();
		if (!saved) {
			return;
		}

		emit('view-deleted', removedViewId);
		deleteDialogOpen.value = false;
		isSelectedViewDirty.value = false;
	} finally {
		isSaving.value = false;
	}
}
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.user-view-control {
	min-width: 0;

	&__select {
		min-width: 180px;
		max-width: 360px;
	}

	@media (max-width: $tablet-breakpoint) {
		&__select {
			min-width: 0;
			max-width: 100%;
		}
	}

	// Readonly on mobile so the native menu stays shut, but it is still the sheet's trigger.
	&__select--sheet {
		:deep(.v-field) {
			cursor: pointer;
		}
	}

	.v-input {
		flex: 1 1 auto;
	}
}

.user-view-control__sheet {
	display: flex;
	flex-direction: column;
	max-height: 85vh;

	.user-view-control__sheet-title {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: $font-size-large;
	}

	.user-view-control__sheet-list {
		overflow-y: auto;

		:deep(.v-list-item--active) {
			background-color: rgba(var(--v-theme-primary), 0.12);
			font-weight: 600;
		}
	}

	.user-view-control__sheet-actions {
		gap: 8px;
		border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
	}
}
</style>
